import { z } from 'zod/v4';

/* -------------------------------------------------------------------------------------------------
 * Shared metering point schemas
 * -----------------------------------------------------------------------------------------------*/

export const meteringPointType = z.enum(['consumption', 'production', 'storage', 'virtual']);
export type MeteringPointType = z.infer<typeof meteringPointType>;

export const minimalMeteringPointSchema = z.object({
  client_reference: z.string(),
  grid_identifier: z.string().nullable().optional(),
  id: z.uuid(),
  type: meteringPointType,
});

export type MinimalMeteringPoint = z.infer<typeof minimalMeteringPointSchema>;

export const meteringPointListResponseSchema = z.object({
  count: z.number(),
  metering_points: z.array(minimalMeteringPointSchema),
});

/* -------------------------------------------------------------------------------------------------
 * Consumer metering point schemas
 * -----------------------------------------------------------------------------------------------*/

export const consumerMeteringPointSchema = z.object({
  aggregation_granularities: z.array(z.string()).nullable().optional(),
  client_reference: z.string(),
  consumer_uuid: z.uuid(),
  grid_identifier: z.string().nullable().optional(),
  measurement_granularities: z.array(z.string()).nullable().optional(),
  uuid: z.uuid(),
});

export type ConsumerMeteringPoint = z.infer<typeof consumerMeteringPointSchema>;

export const consumerMeteringPointListSchema = z.array(consumerMeteringPointSchema);
export type ConsumerMeteringPointList = z.infer<typeof consumerMeteringPointListSchema>;

export const createConsumerMeteringPointSchema = z.object({
  client_reference: z.string(),
  consumer_uuid: z.uuid().optional(),
  grid_identifier: z.string().nullable().optional(),
  measurement_granularity: z.string(),
});

export type CreateConsumerMeteringPoint = z.infer<typeof createConsumerMeteringPointSchema>;

export const updateConsumerMeteringPointSchema = z.object({
  client_reference: z.string(),
  grid_identifier: z.string().nullable().optional(),
});

export type UpdateConsumerMeteringPoint = z.infer<typeof updateConsumerMeteringPointSchema>;

/* -------------------------------------------------------------------------------------------------
 * Production device metering point schemas
 * -----------------------------------------------------------------------------------------------*/

export const productionDeviceMeteringPointSchema = z.object({
  asset_id: z.uuid(),
  client_reference: z.string(),
  grid_identifier: z.string().nullable().optional(),
  id: z.uuid(),
});
export type ProductionDeviceMeteringPoint = z.infer<typeof productionDeviceMeteringPointSchema>;

export const productionDeviceMeteringPointListSchema = z.array(productionDeviceMeteringPointSchema);
export type ProductionDeviceMeteringPointList = z.infer<typeof productionDeviceMeteringPointListSchema>;

export const createProductionDeviceMeteringPointSchema = z.object({
  client_reference: z.string(),
  grid_identifier: z.string().nullable().optional(),
});

export type CreateProductionDeviceMeteringPoint = z.infer<typeof createProductionDeviceMeteringPointSchema>;

export const updateProductionDeviceMeteringPointSchema = z.object({
  client_reference: z.string(),
  grid_identifier: z.string().nullable().optional(),
  id: z.uuid().optional(),
  type: meteringPointType.optional(),
});

export type UpdateProductionDeviceMeteringPoint = z.infer<typeof updateProductionDeviceMeteringPointSchema>;
